import { getErrorMessageOrDefault } from "@vortex/shared";
import PromiseBB from "bluebird";
import update from "immutability-helper";
import * as _ from "lodash";
import * as React from "react";
import { Button } from "react-bootstrap";
import * as ReactDOM from "react-dom";
import { createSelector } from "reselect";
import { collapseGroup, setAttributeFilter, setAttributeSort, setAttributeVisible, setCollapsedGroups, setGroupingAttribute, } from "../actions/tables";
import smoothScroll from "../smoothScroll";
import Debouncer from "../util/Debouncer";
import { log } from "../util/log";
import { getSafe, setSafe } from "../util/storeHelper";
import { makeUnique, sanitizeCSSId, truthy } from "../util/util";
import { ComponentEx, connect, extend, translate } from "./ComponentEx";
import IconBar from "./IconBar";
import GroupingRow, { EMPTY_ID } from "./table/GroupingRow";
import HeaderCell from "./table/HeaderCell";
import { Table, TBody, TD, TH, THead, TR } from "./table/MyTable";
import TableDetail from "./table/TableDetail";
import TableRow from "./table/TableRow";
import ToolbarIcon from "./ToolbarIcon";
import Usage from "./Usage";
/**
 * a wrapper for the react-bootstrap table adding various features:
 * - desktop-like selection/multi-selection
 * - sorting
 * - toggleable columns
 * - a detail-pane that gives additional detail on the (last) selected row
 */
class SuperTable extends ComponentEx {
    static SCROLL_DURATION = 200;
    // delay certain actions (like hiding offscreen items) until after scrolling ends.
    // this improves scroll smoothness at the expense of memory
    static SCROLL_DEBOUNCE = 5000;
    mVisibleAttributes;
    mVisibleDetails;
    mVisibleInlines;
    mPinnedRef;
    mScrollRef;
    mHeaderRef;
    mRowRefs = {};
    mLastSelectOnly = 0;
    mLastDetailIds = [];
    mDetailTimer = null;
    mLastUpdateState = undefined;
    mNextUpdateState = undefined;
    mUpdateInProgress = false;
    mNextState = undefined;
    mNextVisibility = {};
    mDelayedVisibility = {};
    mDelayedVisibilityTimer;
    mProxyHeaderRef;
    mVisibleHeaderRef;
    mHeaderUpdateDebouncer;
    mUpdateCalculatedDebouncer;
    mLastScroll;
    mWillSetVisibility = false;
    mMounted = false;
    mNoShrinkColumns = {};
    mDefaultFilterRef = null;
    mLastScrollLeft = 0;
    mLastOnTop = true;
    constructor(props) {
        super(props);
        this.mNextState = this.state = {
            lastSelected: undefined,
            calculatedValues: undefined,
            rowState: {},
            sortedRows: undefined,
            groupedRows: undefined,
            detailsOpen: false,
            rowIdsDelayed: [],
            rowVisibility: {},
            singleRowActions: this.singleRowActions(props),
            multiRowActions: this.multiRowActions(props),
            columnToggles: this.columnToggles(props),
        };
        const { table, detail, inline } = this.visibleAttributes(props.objects, props.attributeState);
        this.mVisibleAttributes = table;
        this.mVisibleDetails = detail;
        this.mVisibleInlines = inline;
        this.updateCalculatedValues(props).then((didRun) => {
            if (didRun) {
                this.refreshSorted(this.mNextUpdateState);
                this.updateSelection(this.mNextUpdateState);
            }
            return null;
        });
        this.mHeaderUpdateDebouncer = new Debouncer(() => {
            this.updateColumnWidth();
            return PromiseBB.resolve();
        }, 200, false);
        this.mUpdateCalculatedDebouncer = new Debouncer(() => {
            return this.updateCalculatedValues(this.props).then((changedColumns) => {
                this.refreshSorted(this.mNextUpdateState);
                this.updateSelection(this.mNextUpdateState);
                return null;
            });
        }, 200, true);
    }
    componentDidMount() {
        this.updateSelection(this.props);
        const { api } = this.context;
        api.events.on(this.props.tableId + "-scroll-to", this.scrollTo);
        api.events.on(this.props.tableId + "-select-item", this.selectItem);
        this.props.objects.forEach((object) => {
            if (object.externalData !== undefined) {
                object.externalData(() => {
                    this.invalidate(object.id);
                });
            }
        });
        this.mMounted = true;
        window.addEventListener("resize", this.onResize);
    }
    componentWillUnmount() {
        this.context.api.events.removeAllListeners(this.props.tableId + "-scroll-to");
        window.removeEventListener("resize", this.onResize);
        this.mMounted = false;
    }
    UNSAFE_componentWillReceiveProps(newProps) {
        if (newProps.attributeState !== this.props.attributeState ||
            newProps.objects !== this.props.objects) {
            const { attributeState, objects } = newProps;
            const { table, detail, inline } = this.visibleAttributes(objects, attributeState);
            this.mVisibleAttributes = table;
            this.mVisibleDetails = detail;
            this.mVisibleInlines = inline;
            if (Object.keys(newProps.attributeState).find((id) => this.props.attributeState[id] === undefined ||
                this.props.attributeState[id].enabled !==
                    newProps.attributeState[id].enabled)) {
                const columnToggles = this.columnToggles(newProps);
                this.updateState(update(this.mNextState, {
                    columnToggles: { $set: columnToggles },
                }));
            }
        }
        if (newProps.actions !== this.props.actions) {
            this.updateState(update(this.mNextState, {
                singleRowActions: { $set: this.singleRowActions(newProps) },
                multiRowActions: { $set: this.multiRowActions(newProps) },
            }));
        }
        if (newProps.data !== this.props.data) {
            Object.keys(this.mRowRefs).forEach((key) => {
                if (newProps.data[key] === undefined) {
                    delete this.mRowRefs[key];
                    if (this.state.lastSelected !== undefined &&
                        this.state.lastSelected.rowId === key) {
                        this.updateState(update(this.mNextState, { lastSelected: { $set: undefined } }));
                    }
                }
            });
        }
        if (newProps.data !== this.props.data ||
            newProps.dataId !== this.props.dataId ||
            newProps.objects !== this.props.objects) {
            this.mUpdateCalculatedDebouncer.schedule();
        }
        else if (newProps.attributeState !== this.props.attributeState ||
            newProps.language !== this.props.language ||
            newProps.filter !== this.props.filter) {
            this.refreshSorted(newProps);
        }
        else if (newProps.groupBy !== this.props.groupBy ||
            newProps.collapsedGroups !== this.props.collapsedGroups) {
            this.updateState(update(this.mNextState, {
                groupedRows: {
                    $set: this.groupedRows(newProps, this.state.sortedRows),
                },
            }));
        }
    }
    componentDidUpdate() {
        this.mHeaderUpdateDebouncer.schedule();
    }
    shouldComponentUpdate(newProps, newState) {
        return (newState !== this.state ||
            newProps.dataId !== this.props.dataId ||
            newProps.tableId !== this.props.tableId ||
            newProps.actions !== this.props.actions ||
            newProps.attributeState !== this.props.attributeState ||
            newProps.filter !== this.props.filter ||
            newProps.defaultSort !== this.props.defaultSort ||
            newProps.showHeader !== this.props.showHeader ||
            newProps.detailsTitle !== this.props.detailsTitle ||
            newProps.showDetails !== this.props.showDetails ||
            newProps.groupBy !== this.props.groupBy ||
            newProps.collapsedGroups !== this.props.collapsedGroups);
    }
    render() {
        const { showHeader, showDetails, tableId } = this.props;
        const { detailsOpen } = this.state;
        const openClass = detailsOpen ? "open" : "closed";
        const containerClasses = ["table-container"];
        if (showDetails) {
            containerClasses.push("has-details");
        }
        return (React.createElement("div", { className: containerClasses.join(" "), id: `table-${tableId}` },
            React.createElement("div", { className: "table-container-inner" },
                React.createElement("div", { className: "table-main-pane", ref: this.mainPaneRef, tabIndex: 0, onKeyDown: this.handleKeyDown },
                    React.createElement(Table, { hover: true },
                        showHeader === false ? null : this.renderHeader(true),
                        this.renderBody()),
                    this.props.children),
                this.renderFooter()),
            showHeader === false ? null : (React.createElement("div", { className: "table-header-pane", ref: this.mainHeaderRef },
                React.createElement(Table, { hover: true }, this.renderHeader(false)))),
            showDetails === false ? null : (React.createElement("div", { className: `table-details-pane ${openClass}` }, this.renderDetails()))));
    }
    renderHeader(proxy) {
        const { t, data, hasActions } = this.props;
        const { sortedRows } = this.state;
        const filteredLength = sortedRows !== undefined ? sortedRows.length : undefined;
        const totalLength = Object.keys(data).length;
        const actionHeader = this.renderTableActions(hasActions !== false);
        const filterActive = filteredLength !== undefined && filteredLength < totalLength;
        return (React.createElement(THead, { className: "table-header" },
            React.createElement(TR, { domRef: proxy ? this.setProxyHeaderRef : this.setVisibleHeaderRef },
                this.mVisibleAttributes.map((attribute) => this.renderHeaderField(attribute, proxy)),
                actionHeader),
            filterActive ? (React.createElement(TR, { className: "table-pinned", domRef: this.setPinnedRef },
                React.createElement(TD, { colSpan: this.mVisibleAttributes.length + 1 },
                    t("This table is filtered, showing {{shown}}/{{hidden}} items.", { replace: { shown: filteredLength, hidden: totalLength } }),
                    React.createElement(Button, { onClick: this.clearFilters }, t("Clear all filters"))))) : null));
    }
    renderFooter() {
        const { t, tableId } = this.props;
        const { multiRowActions, rowState } = this.state;
        const selected = Object.keys(rowState).filter((key) => rowState[key].selected);
        if (!this.useMultiSelect()) {
            return null;
        }
        if (selected.length < 2) {
            return (React.createElement(Usage, { infoId: "table-multiselect" }, t("Did you know? You can select multiple items using ctrl+click or shift+click or " +
                "select everything using ctrl+a and then do things with all selected items at once.")));
        }
        // the footer itself (.table-footer) is absolutely positioned so it fills out a surrounding
        // panel. To ensure the table body isn't overlapped by the footer, insert a placeholder
        // that needs to be the same size as the footer itself (see css)
        return (React.createElement("div", { className: "table-footer-placeholder" },
            React.createElement("div", { className: "table-footer" },
                React.createElement(IconBar, { className: "menubar", group: `${tableId}-multirow-actions`, groupByIcon: false, instanceId: selected, staticElements: multiRowActions, t: t }),
                React.createElement("div", { className: "menubar" },
                    React.createElement("p", null, t("{{ count }} item selected", { count: selected.length })),
                    React.createElement(ToolbarIcon, { icon: "deselect", key: "btn-deselect", text: t("Deselect All"), onClick: this.deselectAll })))));
    }
    renderBody = () => {
        const { t, attributeState, data } = this.props;
        const { calculatedValues, groupedRows, sortedRows } = this.state;
        if (data === undefined ||
            calculatedValues === undefined ||
            sortedRows === undefined) {
            return React.createElement(TBody, null);
        }
        const sortAttribute = this.mVisibleAttributes.find((attribute) => this.isSortColumn(attributeState[attribute.id]));
        if (groupedRows !== undefined) {
            return (React.createElement(TBody, null, groupedRows.map((group) => {
                const expanded = group.rows !== null;
                const rows = group.rows || [];
                return [
                    React.createElement(GroupingRow, { count: group.count, expanded: expanded, groupId: group.id, groupName: group.name, key: group.id || "__empty", t: t, width: this.mVisibleAttributes.length + 1, onCollapseAll: this.collapseAll, onExpandAll: this.expandAll, onToggle: this.toggleGroup }),
                    ...rows.map((rowId) => this.renderRow(rowId, sortAttribute, group.id)),
                ];
            })));
        }
        else {
            return (React.createElement(TBody, null, sortedRows.map((row, idx) => this.renderRow(row, sortAttribute))));
        }
    };
    toggleGroup = (groupName, expand) => {
        const { onCollapseGroup, tableId } = this.props;
        onCollapseGroup(tableId, groupName, !expand);
    };
    expandAll = () => {
        const { onSetCollapsedGroups, tableId } = this.props;
        onSetCollapsedGroups(tableId, []);
    };
    collapseAll = () => {
        const { groupBy, onSetCollapsedGroups, tableId } = this.props;
        const { calculatedValues, sortedRows } = this.state;
        const groupOptions = makeUnique(sortedRows.map((rowId) => calculatedValues[rowId][groupBy]));
        onSetCollapsedGroups(tableId, groupOptions);
    };
    scrollTo = (id, mayRetry) => {
        try {
            const node = ReactDOM.findDOMNode(this.mRowRefs[id]);
            if (node !== null) {
                this.scrollToItem(node, false);
            }
            else if (mayRetry !== false) {
                setTimeout(() => {
                    this.scrollTo(id, false);
                }, 2000);
            }
            else {
                log("warn", "node not found", id);
            }
        }
        catch (err) {
            if (mayRetry !== false) {
                setTimeout(() => {
                    this.scrollTo(id, false);
                }, 2000);
            }
            else {
                log("warn", "failed to scroll to item", {
                    id,
                    tableId: this.props.tableId,
                    error: getErrorMessageOrDefault(err),
                });
            }
        }
    };
    selectItem = (id, openDetails) => {
        this.scrollTo(id);
        this.setGroup(undefined);
        this.selectOnly(id, undefined, false);
        this.updateState(update(this.mNextState, {
            detailsOpen: { $set: openDetails },
        }), this.onRowStateChanged);
    };
    getGroupOptions(props, sortedRows, sortAttribute, groupAttribute, valFunc) {
        const { attributeState, data } = props;
        let arrays = false;
        let compare;
        const groupOptions = makeUnique(sortedRows.reduce((prev, rowId) => {
            if (data[rowId] === undefined) {
                return prev;
            }
            const value = valFunc(rowId);
            if (Array.isArray(value)) {
                arrays = true;
                prev.push(...value);
            }
            else {
                prev.push(value);
            }
            return prev;
        }, [])).sort((lhs, rhs) => {
            if (compare === undefined) {
                const desc = sortAttribute !== undefined &&
                    sortAttribute.id === groupAttribute.id &&
                    attributeState[sortAttribute.id]?.sortDirection === "desc";
                if (typeof lhs === "number") {
                    compare = desc ? (l, r) => r - l : (l, r) => l - r;
                }
                else {
                    compare = desc
                        ? (l, r) => r
                            .toString()
                            .toLowerCase()
                            .localeCompare(l.toString().toLowerCase())
                        : (l, r) => l
                            .toString()
                            .toLowerCase()
                            .localeCompare(r.toString().toLowerCase());
                }
            }
            return compare(lhs, rhs);
        });
        if (arrays) {
            groupOptions.push(EMPTY_ID);
        }
        return groupOptions;
    }
    invalidate(columnId) {
        this.updateCalculatedValues(this.props, columnId);
    }
    toggleDetails = () => {
        const { detailsOpen } = this.state;
        this.updateState(update(this.mNextState, { detailsOpen: { $set: !detailsOpen } }));
    };
    setRowState(rowIds) {
        const { data } = this.props;
        const filteredRowIds = rowIds.filter((id) => this.state.calculatedValues[id] !== undefined && data[id] !== undefined);
        this.updateState(update(this.mNextState, { rowIdsDelayed: { $set: filteredRowIds } }));
        this.mDetailTimer = null;
    }
    updateDetailIds(rowIds) {
        if (_.isEqual(this.mLastDetailIds, rowIds)) {
            return;
        }
        this.mLastDetailIds = rowIds;
        if (this.mDetailTimer !== null) {
            clearTimeout(this.mDetailTimer);
        }
        this.mDetailTimer = setTimeout(() => this.setRowState(rowIds), 200);
    }
    renderDetails = () => {
        const { t, data, detailsTitle, language, objects } = this.props;
        const { calculatedValues, detailsOpen, rowIdsDelayed } = this.state;
        if (rowIdsDelayed === undefined ||
            rowIdsDelayed.length === 0 ||
            calculatedValues === undefined ||
            data === undefined) {
            return null;
        }
        return (React.createElement(TableDetail, { attributes: this.mVisibleDetails, language: language, rawData: data, rowData: calculatedValues, rowIds: rowIdsDelayed, show: detailsOpen, t: t, title: detailsTitle, onToggleShow: this.toggleDetails }));
    };
    columnToggles(props) {
        const { t, columnBlacklist, objects } = props;
        const result = [];
        const blacklist = new Set(columnBlacklist || []);
        const { columns, inlines, disabled } = objects.reduce((prev, attr) => {
            if (attr.isToggleable && !blacklist.has(attr.id)) {
                const visible = attr.condition?.() ?? true;
                const attributeState = this.getAttributeState(attr, props.attributeState);
                prev[attr.placement === "inline"
                    ? "inlines"
                    : visible
                        ? "columns"
                        : "disabled"].push({
                    icon: attributeState.enabled
                        ? "checkbox-checked"
                        : "checkbox-unchecked",
                    title: attr.name,
                    action: (arg) => this.setAttributeVisible(attr.id, !attributeState.enabled),
                });
            }
            return prev;
        }, { columns: [], inlines: [], disabled: [] });
        if (columns.length > 0) {
            result.push({
                icon: null,
                title: "Toggle Columns",
            }, ...columns);
        }
        if (inlines.length > 0) {
            result.push({
                icon: null,
                title: "Toggle Inlines",
            }, ...inlines);
        }
        /* currently not showing disabled columns at all, that's probably the more
           intuitive solution
        if (disabled.length > 0) {
          result.push({
            icon: null,
            title: t('Toggle Disabled Columns'),
          }, ...disabled);
        }
        */
        return result.map((res, idx) => ({ ...res, position: idx }));
    }
    renderTableActions(hasActions) {
        const { t, tableId } = this.props;
        const { columnToggles } = this.state;
        return (React.createElement(TH, { className: `table-${tableId} header-action` },
            React.createElement("div", null,
                hasActions ? (React.createElement("div", { className: "header-action-label" }, t("Actions"))) : null,
                columnToggles.length > 0 ? (React.createElement(IconBar, { className: "table-actions", collapse: "force", group: `${tableId}-action-icons-multi`, icon: "settings", id: `${tableId}-tableactions`, staticElements: columnToggles, t: t })) : null)));
    }
    isSortColumn(attributeState) {
        return (attributeState !== undefined &&
            attributeState.sortDirection !== undefined &&
            attributeState.sortDirection !== "none");
    }
    renderRow(rowId, sortAttribute, groupId) {
        const { t, attributeState, data, language, hasActions, objects, tableId } = this.props;
        const { calculatedValues, rowState, rowVisibility, singleRowActions } = this.state;
        if (calculatedValues[rowId] === undefined || data[rowId] === undefined) {
            return null;
        }
        const attributes = this.mVisibleAttributes;
        const extraClasses = this.props.objects
            .map((attr) => attr.cssClass?.(data[rowId], this.getAttributeState(attr, attributeState).enabled))
            .filter((cls) => truthy(cls));
        extraClasses.push(sanitizeCSSId(rowId));
        const tableRowId = sanitizeCSSId(rowId + "_" + (groupId || ""));
        return (React.createElement(TableRow, { actions: singleRowActions, attributes: attributes, container: this.mScrollRef, data: calculatedValues[rowId], domRef: this.setRowRef, group: groupId, grouped: groupId !== undefined, hasActions: hasActions !== undefined ? hasActions : true, highlighted: getSafe(rowState, [rowId, "highlighted"], false), id: tableRowId, inlines: this.mVisibleInlines, key: tableRowId, language: language, rawData: data[rowId], rowClasses: extraClasses, selected: getSafe(rowState, [rowId, "selected"], false), sortAttribute: sortAttribute !== undefined ? sortAttribute.id : undefined, t: t, tableId: tableId, visible: rowVisibility[tableRowId] === true, onClick: this.selectRow, onHighlight: this.setRowHighlight, onSetVisible: this.setRowVisible }));
    }
    filterSet(filt) {
        return truthy(filt) && (!Array.isArray(filt) || filt.length !== 0);
    }
    renderHeaderField = (attribute, proxy) => {
        const { t, filter, groupBy } = this.props;
        const attributeState = this.getAttributeState(attribute);
        const filt = attribute.filter !== undefined && filter !== undefined
            ? (filter[attribute.id] ?? null)
            : undefined;
        if (attributeState.enabled) {
            const classes = [`header-${attribute.id}`];
            if (this.filterSet(filt) &&
                (attribute.filter.isEmpty === undefined ||
                    !attribute.filter.isEmpty(filt))) {
                classes.push("table-filter-column");
            }
            if (this.isSortColumn(attributeState)) {
                classes.push("table-sort-column");
            }
            return (React.createElement(HeaderCell, { attribute: attribute, className: classes.join(" "), doFilter: true, doGroup: attribute.id === groupBy, key: attribute.id, ref: proxy ? this.setHeaderCellRef : undefined, state: attributeState, t: t, onSetFilter: this.setFilter, onSetGroup: this.setGroup, onSetSortDirection: this.setSortDirection }, attribute.filter !== undefined ? (React.createElement(attribute.filter.component, { attributeId: attribute.id, domRef: attribute.isDefaultFilter ? this.setDefaultFilterRef : undefined, filter: filt, t: t, onSetFilter: this.setFilter })) : null));
        }
        else {
            return null;
        }
    };
    setDefaultFilterRef = (ref) => {
        this.mDefaultFilterRef = ref;
    };
    setHeaderCellRef = (ref) => {
        if (ref !== null) {
            if (ref.props.attribute.noShrink === true) {
                this.mNoShrinkColumns[ref.props.attribute.id] = ref;
            }
        }
    };
    handleKeyDown = (evt) => {
        const { lastSelected, selectionStart, sortedRows } = this.state;
        try {
            if (evt.target !== this.mScrollRef) {
                return;
            }
            if (evt.key === "f" && evt.ctrlKey) {
                if (this.mDefaultFilterRef !== null) {
                    this.mDefaultFilterRef.focus();
                }
                return;
            }
            if (this.useMultiSelect()) {
                if (evt.key === "a" && evt.ctrlKey) {
                    this.selectAll();
                    return;
                }
                if (evt.shiftKey && selectionStart === undefined) {
                    const selection = lastSelected !== undefined
                        ? { rowId: lastSelected.rowId, groupId: lastSelected.groupId }
                        : undefined;
                    this.updateState(update(this.mNextState, {
                        selectionStart: { $set: selection },
                    }));
                }
                else if (!evt.shiftKey && selectionStart !== undefined) {
                    this.updateState(update(this.mNextState, {
                        selectionStart: { $set: undefined },
                    }));
                }
            }
            // TODO: this calculation of the number of lines visible in the table is only
            // accurate under the assumption all lines have the same height
            let visibleLineCount = 0;
            if (lastSelected !== undefined &&
                this.mRowRefs[lastSelected.rowId] !== undefined) {
                // the previously selected row might no longer be visible, which would cause
                // an exception when trying to find the associated dom node
                const lastIdx = sortedRows.indexOf(lastSelected.rowId);
                if (lastIdx !== -1) {
                    const selectedNode = ReactDOM.findDOMNode(this.mRowRefs[lastSelected.rowId]);
                    visibleLineCount =
                        this.mScrollRef.clientHeight / selectedNode.clientHeight;
                    // account for the header. quite inaccurate.
                    visibleLineCount -= 2;
                }
            }
            let offset = 0;
            switch (evt.keyCode) {
                case 32: {
                    evt.preventDefault();
                    this.toggleDetails();
                    break;
                }
                case 33:
                    offset = Math.round(visibleLineCount * -0.5);
                    break;
                case 34:
                    offset = Math.round(visibleLineCount * 0.5);
                    break;
                case 38:
                    offset = -1;
                    break;
                case 40:
                    offset = 1;
                    break;
            }
            if (offset !== 0) {
                evt.preventDefault();
                const groupId = evt.currentTarget.getAttribute("data-group") || undefined;
                const newItem = this.selectRelative(offset, groupId, evt.shiftKey);
                if (this.mRowRefs[newItem] !== undefined && this.mMounted) {
                    this.scrollToItem(ReactDOM.findDOMNode(this.mRowRefs[newItem]), Math.abs(offset) > 1);
                }
            }
            else {
                const action = this.props.actions.find((iter) => this.matchHotKey(iter, evt.keyCode, evt.shiftKey, evt.altKey, evt.ctrlKey));
                if (action !== undefined) {
                    evt.preventDefault();
                    const { rowState } = this.state;
                    action.action(Object.keys(rowState).filter((id) => rowState[id].selected));
                }
            }
        }
        catch (err) {
            log("warn", "failed to handle keydown event", err);
        }
    };
    matchHotKey(action, code, shift, alt, ctrl) {
        return (action.hotKey !== undefined &&
            action.hotKey.code === code &&
            (action.hotKey.shift || shift === false) &&
            (action.hotKey.alt || alt === false) &&
            (action.hotKey.ctrl || ctrl === false));
    }
    refreshSorted(props) {
        const { data, language } = props;
        if (this.state.calculatedValues === undefined) {
            return;
        }
        const filtered = this.filteredRows(props, this.mVisibleAttributes, data);
        const attrState = this.getAttributeStates(props);
        const sortedRows = this.sortedRows(attrState, this.mVisibleAttributes, filtered, language);
        const groupedRows = this.groupedRows(props, sortedRows);
        this.updateState(update(this.mNextState, {
            sortedRows: { $set: sortedRows },
            groupedRows: { $set: groupedRows },
        }));
    }
    getAttributeStates(props) {
        return Object.keys(props.attributeState ?? {}).length > 0 ||
            this.mVisibleAttributes === undefined
            ? props.attributeState
            : this.mVisibleAttributes.reduce((prev, attribute) => {
                if (attribute.isDefaultSort === true) {
                    prev[attribute.id] = { sortDirection: "asc" };
                }
                else if (attribute.isDefaultSort === "desc") {
                    prev[attribute.id] = { sortDirection: "desc" };
                }
                return prev;
            }, {});
    }
    singleRowActions(props) {
        return props.actions.filter((action) => action.singleRowAction ?? true);
    }
    multiRowActions(props) {
        return props.actions.filter((action) => action.multiRowAction ?? true);
    }
    selectRelative = (delta, groupId, shiftHeld) => {
        const { groupBy } = this.props;
        const { groupedRows, lastSelected, selectionStart, sortedRows } = this.state;
        if (lastSelected === undefined || sortedRows === undefined) {
            return;
        }
        let groupSortedRows;
        if (groupBy !== undefined) {
            groupSortedRows = groupedRows.reduce((prev, group) => {
                prev.push(...(group.rows || []));
                return prev;
            }, []);
        }
        else {
            groupSortedRows = sortedRows;
        }
        let idx = groupSortedRows.indexOf(lastSelected.rowId);
        const oldIdx = idx;
        idx = Math.min(Math.max(idx + delta, 0), groupSortedRows.length - 1);
        const newSelection = groupSortedRows[idx];
        if (oldIdx === idx) {
            return newSelection;
        }
        this.selectOnly(newSelection, groupId, false);
        if (shiftHeld) {
            this.selectTo(selectionStart.rowId, selectionStart.groupId);
        }
        return newSelection;
    };
    setProxyHeaderRef = (ref) => {
        this.mProxyHeaderRef = ref;
        this.mHeaderUpdateDebouncer.schedule();
    };
    setVisibleHeaderRef = (ref) => {
        this.mVisibleHeaderRef = ref;
        this.mHeaderUpdateDebouncer.schedule();
    };
    mLastHeaderHeight = 0;
    updateColumnWidth() {
        if (!truthy(this.mProxyHeaderRef) || !truthy(this.mVisibleHeaderRef)) {
            return;
        }
        this.mProxyHeaderRef.childNodes.forEach((node, index) => {
            this.mVisibleHeaderRef.childNodes.item(index).style.minWidth = this.mVisibleHeaderRef.childNodes.item(index).style.maxWidth = `${node.clientWidth}px`;
        });
        const height = this.mVisibleHeaderRef.clientHeight;
        if (height !== this.mLastHeaderHeight) {
            this.mScrollRef.style["marginTop"] = `${height}px`;
            this.mLastHeaderHeight = height;
        }
    }
    setPinnedRef = (ref) => {
        this.mPinnedRef = ref;
    };
    setRowRef = (ref) => {
        if (ref !== null) {
            this.mRowRefs[ref.props["data-rowid"]] = ref;
        }
    };
    setRowVisible = (rowId, visible) => {
        // turn rows visible asap, turning them invisible can be done when scrolling ends
        // Important: This was intended as a performance optimisation but it also fixed
        //   a problem where drag&drop connectors between rows far apart didn't work because
        //   the source was hidden before while dragging.
        if (visible || this.mDelayedVisibilityTimer === undefined) {
            this.mNextVisibility[rowId] = visible;
            this.mDelayedVisibility[rowId] = visible;
            // it's possible that a previous visibility change hadn't even been
            // rendered yet, in this case we don't have to trigger an update
            if (visible !== this.state.rowVisibility[rowId]) {
                this.triggerUpdateVisibility();
            }
        }
        else {
            this.mDelayedVisibility[rowId] = visible;
        }
    };
    triggerUpdateVisibility() {
        if (!this.mWillSetVisibility) {
            this.mWillSetVisibility = true;
            window.requestAnimationFrame(() => {
                this.mWillSetVisibility = false;
                this.updateState(setSafe(this.mNextState, ["rowVisibility"], {
                    ...this.mNextVisibility,
                }));
            });
        }
    }
    postScroll() {
        if (Date.now() - this.mLastScroll < SuperTable.SCROLL_DEBOUNCE) {
            this.mDelayedVisibilityTimer = setTimeout(() => this.postScroll(), SuperTable.SCROLL_DEBOUNCE + 100);
        }
        else {
            this.mDelayedVisibilityTimer = undefined;
            this.mNextVisibility = { ...this.mDelayedVisibility };
            this.triggerUpdateVisibility();
        }
    }
    setRowHighlight = (rowId, highlighted) => {
        this.updateState(setSafe(this.mNextState, ["rowState", rowId, "highlighted"], highlighted));
    };
    scrollToItem = (item, smooth, iterations = 3) => {
        const height = this.mScrollRef.offsetHeight;
        const offset = height / 5;
        const topLimit = this.mScrollRef.scrollTop + offset;
        const bottomLimit = this.mScrollRef.scrollTop + this.mScrollRef.clientHeight - offset;
        const itemBottom = item.offsetTop + item.offsetHeight;
        let targetPos;
        if (item.offsetTop < topLimit) {
            targetPos = Math.max(item.offsetTop - offset, 0);
        }
        else if (itemBottom > bottomLimit) {
            targetPos = itemBottom - this.mScrollRef.clientHeight + offset;
        }
        if (targetPos !== undefined && targetPos !== this.mScrollRef.scrollTop) {
            if (smooth) {
                smoothScroll(this.mScrollRef, targetPos, SuperTable.SCROLL_DURATION).then((cont) => cont && iterations > 0
                    ? this.scrollToItem(item, false, iterations - 1)
                    : PromiseBB.resolve());
            }
            else {
                this.mScrollRef.scrollTop = targetPos;
                if (iterations > 0) {
                    // workaround: since we're not rendering off-screen rows it's possible for row heights to
                    //  change as we scroll and then the offset we calculated might not be in the visible
                    //  range after all.
                    setTimeout(() => {
                        this.scrollToItem(item, smooth, iterations - 1);
                    }, 100);
                }
            }
        }
    };
    onScroll = (event) => {
        this.mLastScroll = Date.now();
        if (this.mDelayedVisibilityTimer === undefined) {
            this.mDelayedVisibilityTimer = setTimeout(() => this.postScroll(), SuperTable.SCROLL_DEBOUNCE + 100);
        }
        const ele = event.target;
        const atTop = ele.scrollTop === 0;
        if ((ele.scrollLeft !== this.mLastScrollLeft && truthy(this.mHeaderRef)) ||
            (atTop !== this.mLastOnTop && truthy(this.mPinnedRef))) {
            this.mLastOnTop = atTop;
            this.mLastScrollLeft = ele.scrollLeft;
            window.requestAnimationFrame(() => {
                if (truthy(this.mPinnedRef)) {
                    this.mPinnedRef.className = atTop
                        ? "table-pinned"
                        : "table-pinned-hidden";
                }
                if (truthy(this.mHeaderRef)) {
                    this.mHeaderRef.style.left = `-${ele.scrollLeft}px`;
                }
            });
        }
        Object.keys(this.mNoShrinkColumns).forEach((colId) => {
            this.mNoShrinkColumns[colId].updateWidth();
        });
    };
    onResize = () => {
        this.mHeaderUpdateDebouncer.schedule();
    };
    mainPaneRef = (ref) => {
        if (ref === null) {
            return;
        }
        // not sure if this is necessary, I guess not
        ref.removeEventListener("scroll", this.onScroll);
        // translate the header so that it remains in view during scrolling
        ref.addEventListener("scroll", this.onScroll);
        this.mScrollRef = ref;
    };
    mainHeaderRef = (ref) => {
        this.mHeaderRef = ref;
    };
    updateCalculatedValues(props, forceUpdateId) {
        this.mNextUpdateState = props;
        if (this.mUpdateInProgress) {
            return PromiseBB.resolve([]);
        }
        this.mUpdateInProgress = true;
        const { t, data, objects } = props;
        // keep track of which columns had data changed so that we can later figure out if
        // sorting needs to be updated
        const changedColumns = new Set();
        const oldState = this.mLastUpdateState || { data: {} };
        let newValues = this.state.calculatedValues || {};
        // recalculate each attribute in each row
        return PromiseBB.map(Object.keys(data), (rowId) => {
            const delta = {};
            return PromiseBB.map(objects, (attribute) => {
                // avoid recalculating if the source data hasn't changed. To support
                // isVolatile we still go through each attribute even if the entire row didn't change
                if (!attribute.isVolatile &&
                    attribute.id !== forceUpdateId &&
                    oldState.data[rowId] === data[rowId]) {
                    return PromiseBB.resolve();
                }
                if (attribute.calc === undefined) {
                    return PromiseBB.resolve();
                }
                return PromiseBB.resolve(attribute.calc(data[rowId], t))
                    .then((newValue) => {
                    if (!_.isEqual(newValue, getSafe(newValues, [rowId, attribute.id], undefined))) {
                        changedColumns.add(attribute.id);
                        delta[attribute.id] = newValue;
                    }
                    return null;
                })
                    .catch((err) => {
                    log("error", "failed to calculate attribute value", {
                        attribute: attribute.id,
                        row: rowId,
                        error: getErrorMessageOrDefault(err),
                    });
                });
            }).then(() => {
                if (Object.keys(delta).length > 0) {
                    delta.__id = rowId;
                    if (newValues[rowId] === undefined) {
                        newValues[rowId] = delta;
                    }
                    else {
                        newValues = update(newValues, { [rowId]: { $merge: delta } });
                    }
                }
            });
        })
            .then(() => PromiseBB.map(Object.keys(oldState.data), (rowId) => {
            if (data[rowId] === undefined) {
                delete newValues[rowId];
            }
        }))
            .then(() => 
        // once everything is recalculated, update the cache
        new PromiseBB((resolve, reject) => {
            this.updateState(update(this.mNextState, {
                calculatedValues: { $set: newValues },
            }), () => resolve());
        }))
            .then(() => {
            const { rowState } = this.state;
            return this.updateDetailIds(Object.keys(rowState).filter((id) => rowState[id].selected));
        })
            .then(() => {
            this.mUpdateInProgress = false;
            this.mLastUpdateState = props;
            if (this.mNextUpdateState !== this.mLastUpdateState) {
                // another update was queued while this was active
                return this.updateCalculatedValues(this.mNextUpdateState);
            }
            else {
                return PromiseBB.resolve(Array.from(changedColumns));
            }
        })
            .catch((err) => {
            this.mUpdateInProgress = false;
            return PromiseBB.reject(err);
        });
    }
    updateSelection(props) {
        // unselect rows that are no longer in the data
        const changes = {};
        const selected = [];
        Object.keys(this.state.rowState).forEach((rowId) => {
            if (this.state.rowState[rowId].selected) {
                if (props.data[rowId] === undefined) {
                    changes[rowId] = { selected: { $set: false } };
                }
                else {
                    selected.push(rowId);
                }
            }
        });
        if (!_.isEqual(this.mNextState.rowState, changes)) {
            this.updateState(update(this.mNextState, { rowState: changes }), this.onRowStateChanged);
        }
    }
    standardSort(lhs, rhs) {
        return lhs < rhs ? -1 : lhs === rhs ? 0 : 1;
    }
    filteredRows(props, attributes, data) {
        const { filter } = props;
        const { calculatedValues } = this.state;
        if (filter === undefined) {
            return data;
        }
        const result = {};
        Object.keys(calculatedValues)
            .filter((rowId) => {
            // filter out rows which no longer exist
            if (data[rowId] === undefined) {
                return false;
            }
            // return only elements for which we can't find a non-matching filter
            // (in other words: Keep only those items that match all filters)
            return (attributes.find((attribute) => {
                if (attribute.filter === undefined) {
                    return false;
                }
                const dataId = attribute.filter.dataId || attribute.id;
                // raw can be true, false or a string that specifies an attribute that is
                // different from the one for which the filter is set. The raw value of that
                // attribute is then used for the filter
                const value = attribute.filter.raw !== false
                    ? attribute.filter.raw === true
                        ? dataId === "$"
                            ? data[rowId]
                            : data[rowId][dataId]
                        : (data[rowId][attribute.filter.raw] || {})[dataId]
                    : calculatedValues[rowId][dataId];
                return (filter[attribute.id] !== undefined &&
                    filter[attribute.id] !== null &&
                    !attribute.filter.matches(filter[attribute.id], value, this.context.api.store.getState()));
            }) === undefined);
        })
            .forEach((key) => (result[key] = data[key]));
        return result;
    }
    attributeSortFunction(sortAttribute, calculatedValues, data, locale) {
        let sortFunction;
        if (sortAttribute.sortFunc !== undefined) {
            sortFunction = (lhsId, rhsId) => sortAttribute.sortFunc(calculatedValues[lhsId][sortAttribute.id], calculatedValues[rhsId][sortAttribute.id], locale);
        }
        else if (sortAttribute.sortFuncRaw !== undefined) {
            sortFunction = (lhsId, rhsId) => sortAttribute.sortFuncRaw(data[lhsId], data[rhsId], locale);
        }
        else {
            sortFunction = (lhsId, rhsId) => this.standardSort(calculatedValues[lhsId][sortAttribute.id], calculatedValues[rhsId][sortAttribute.id]);
        }
        return sortFunction;
    }
    sortedRows(attributeState, attributes, data, locale) {
        const { calculatedValues } = this.state;
        const sortAttribute = attributes.find((attribute) => {
            return (attributeState[attribute.id] !== undefined &&
                attributeState[attribute.id].sortDirection !== undefined &&
                attributeState[attribute.id].sortDirection !== "none");
        });
        // return unsorted if no sorting column was selected or if the values
        // haven't been calculated yet
        if (sortAttribute === undefined) {
            return (Object.keys(data)
                // catch cases where input data was broken. Code is usually not
                // equipped to deal with undefined row data
                .filter((rowId) => data[rowId] !== undefined));
        }
        const sortFunction = this.attributeSortFunction(sortAttribute, calculatedValues, data, locale);
        const descending = attributeState[sortAttribute.id].sortDirection === "desc";
        const dataIds = Object.keys(data).filter((key) => calculatedValues[key] !== undefined);
        // comparison function if either value or both is/are undefined
        const undefCompare = (lhsId, rhsId) => calculatedValues[lhsId][sortAttribute.id] !== undefined
            ? 1
            : calculatedValues[rhsId][sortAttribute.id] !== undefined
                ? -1
                : 0;
        return dataIds.sort((lhsId, rhsId) => {
            const res = sortAttribute.sortFuncRaw !== undefined ||
                (calculatedValues[lhsId][sortAttribute.id] !== undefined &&
                    calculatedValues[rhsId][sortAttribute.id] !== undefined)
                ? sortFunction(lhsId, rhsId)
                : undefCompare(lhsId, rhsId);
            return descending ? res * -1 : res;
        });
    }
    groupedRows(props, sortedRows) {
        const { t, attributeState, collapsedGroups, data, groupBy } = props;
        const { calculatedValues } = this.state;
        const groupAttribute = this.mVisibleAttributes.find((attribute) => attribute.id === groupBy);
        if (groupAttribute === undefined) {
            return undefined;
        }
        if (sortedRows === undefined) {
            return [];
        }
        const sortAttribute = this.mVisibleAttributes.find((attribute) => this.isSortColumn(attributeState[attribute.id]));
        const valFunc = (rowId) => typeof groupAttribute.isGroupable === "function"
            ? groupAttribute.isGroupable(data[rowId], t)
            : calculatedValues[rowId]?.[groupAttribute.id] || "";
        const groupOptions = this.getGroupOptions(this.props, sortedRows, sortAttribute, groupAttribute, valFunc);
        return groupOptions.reduce((prev, group) => {
            const groupItems = sortedRows.filter((row) => {
                if (data[row] === undefined) {
                    return prev;
                }
                const rowVal = valFunc(row);
                if (Array.isArray(rowVal)) {
                    if (rowVal.length === 0) {
                        return group === EMPTY_ID;
                    }
                    return rowVal.indexOf(group) !== -1;
                }
                else {
                    return rowVal === group;
                }
            });
            if (groupItems.length !== 0) {
                const expanded = collapsedGroups.indexOf(group || "") === -1;
                const groupName = groupAttribute.groupName !== undefined
                    ? groupAttribute.groupName(group)
                    : group;
                prev.push({
                    id: group,
                    name: groupName,
                    count: groupItems.length,
                    rows: expanded ? groupItems : null,
                });
            }
            return prev;
        }, []);
    }
    setAttributeVisible = (attributeId, visible) => {
        const { onSetAttributeVisible, tableId } = this.props;
        onSetAttributeVisible(tableId, attributeId, visible);
    };
    getClasses(element) {
        // because classname is supposed to be a string but on svg elements
        // it may be SVGAnimatedString
        const classAny = element.className;
        return classAny === undefined
            ? ""
            : classAny instanceof SVGAnimatedString
                ? classAny.baseVal
                : element.className;
    }
    selectRow = (evt) => {
        if (evt.isDefaultPrevented()) {
            return;
        }
        const { selectionStart } = this.state;
        if (!evt.shiftKey && selectionStart !== undefined) {
            this.updateState(update(this.mNextState, {
                selectionStart: { $set: undefined },
            }));
        }
        let iter = evt.target;
        while (iter !== null &&
            iter !== undefined &&
            iter.tagName !== "BUTTON" &&
            this.getClasses(iter).split(" ").indexOf("xtd") === -1) {
            iter = iter.parentNode;
        }
        if (iter !== null && iter.tagName === "BUTTON") {
            // don't handle if the click was on a button
            return;
        }
        const row = evt.currentTarget;
        const rowId = row.getAttribute("data-rowid");
        const groupId = row.getAttribute("data-group");
        if (this.useMultiSelect() && evt.ctrlKey) {
            // ctrl-click -> toggle the selected row, leave remaining selection intact
            this.selectToggle(rowId, groupId);
        }
        else if (this.useMultiSelect() && evt.shiftKey) {
            // shift-click -> select everything between this row and the last one clicked,
            //                deselect everything else
            this.selectTo(rowId, groupId);
        }
        else {
            // regular click -> select only the clicked row, everything else get deselected
            this.selectOnly(rowId, groupId, true);
        }
        // Focus the table container to enable keyboard shortcuts (CTRL+A, arrow keys, etc.)
        // but not when the click landed inside an interactive control, otherwise we
        // blur react-select inputs (and similar) on mouseup, which slams dropdown menus
        // shut before the user can pick an option.
        const target = evt.target;
        if (!target?.closest?.('input, select, textarea, [contenteditable="true"], .Select')) {
            this.mScrollRef?.focus();
        }
    };
    selectOnly(rowId, groupId, click) {
        const rowState = {};
        Object.keys(this.state.rowState).forEach((iterId) => {
            rowState[iterId] = { selected: { $set: false } };
        });
        rowState[rowId] =
            this.state.rowState[rowId] === undefined
                ? { $set: { selected: true } }
                : { selected: { $set: true } };
        const now = Date.now();
        if (click &&
            this.state.lastSelected !== undefined &&
            this.state.lastSelected.rowId === rowId &&
            now - this.mLastSelectOnly < 500) {
            this.updateState(update(this.mNextState, {
                detailsOpen: { $set: !this.state.detailsOpen },
                rowState,
            }), this.onRowStateChanged);
        }
        else {
            if (click) {
                this.mLastSelectOnly = now;
            }
            this.updateState(update(this.mNextState, {
                lastSelected: { $set: { rowId, groupId } },
                rowState,
            }), this.onRowStateChanged);
        }
    }
    selectToggle(rowId, groupId) {
        const wasSelected = getSafe(this.state.rowState, [rowId, "selected"], undefined);
        if (!wasSelected) {
            const rowState = wasSelected === undefined
                ? { $set: { selected: true } }
                : { selected: { $set: !wasSelected } };
            this.updateState(update(this.mNextState, {
                lastSelected: { $set: { rowId, groupId } },
                rowState: { [rowId]: rowState },
            }), this.onRowStateChanged);
        }
        else {
            this.updateState(update(this.mNextState, {
                rowState: { [rowId]: { selected: { $set: !wasSelected } } },
            }), this.onRowStateChanged);
        }
    }
    onRowStateChanged = () => {
        const { onChangeSelection } = this.props;
        const { rowState } = this.state;
        const selectedIds = Object.keys(rowState).filter((id) => rowState[id].selected);
        this.updateDetailIds(selectedIds);
        onChangeSelection?.(selectedIds);
    };
    deselectAll = () => {
        const { rowState } = this.state;
        const newState = {};
        Object.keys(rowState).forEach((key) => {
            if (rowState[key].selected) {
                newState[key] = { selected: { $set: false } };
            }
        });
        this.updateState(update(this.mNextState, { rowState: newState }), this.onRowStateChanged);
    };
    selectAll() {
        const { sortedRows, rowState } = this.state;
        const newState = {};
        // first, disable what's currently selected
        Object.keys(rowState).forEach((key) => {
            if (rowState[key].selected) {
                newState[key] = { selected: { $set: false } };
            }
        });
        // then (re-)enable all visible selections
        (sortedRows || []).forEach((key) => {
            newState[key] =
                newState[key] === undefined
                    ? { $set: { selected: true } }
                    : { selected: { $set: true } };
        });
        this.updateState(update(this.mNextState, { rowState: newState }), this.onRowStateChanged);
    }
    selectTo(rowId, groupId) {
        const { objects } = this.props;
        const { lastSelected, groupedRows, sortedRows } = this.mNextState;
        if (lastSelected === undefined) {
            return;
        }
        let groupBy = this.props.groupBy;
        // if the grouping attribute was provided by an extension it might have become unavailable
        const groupAttribute = groupBy !== undefined
            ? objects.find((attribute) => attribute.id === groupBy)
            : undefined;
        const groupByState = groupAttribute !== undefined
            ? this.getAttributeState(groupAttribute)
            : undefined;
        if (groupBy !== undefined && !groupByState?.enabled) {
            groupBy = undefined;
        }
        let groupSortedRows;
        if (groupBy !== undefined && groupedRows !== undefined) {
            groupSortedRows = groupedRows.reduce((prev, group) => {
                prev.push(...((group.rows || []).map((rId) => ({
                    rowId: rId,
                    groupId: group.id,
                })) || []));
                return prev;
            }, []);
        }
        else {
            groupSortedRows = sortedRows.map((rId) => ({
                rowId: rId,
                groupId: undefined,
            }));
        }
        const selection = new Set([rowId, lastSelected.rowId]);
        let selecting = false;
        groupSortedRows.forEach((iterId) => {
            let isBracket = (iterId.rowId === rowId && (!groupBy || iterId.groupId === groupId)) ||
                (iterId.rowId === lastSelected.rowId &&
                    (!groupBy || iterId.groupId === lastSelected.groupId));
            if (!selecting && isBracket) {
                selecting = true;
                isBracket =
                    rowId === lastSelected.rowId && groupId === lastSelected.groupId;
            }
            if (selecting) {
                selection.add(iterId.rowId);
                if (isBracket) {
                    selecting = false;
                }
            }
        });
        const rowState = {};
        groupSortedRows.forEach((iterId) => {
            rowState[iterId.rowId] =
                this.state.rowState[iterId.rowId] === undefined
                    ? { $set: { selected: selection.has(iterId.rowId) } }
                    : { selected: { $set: selection.has(iterId.rowId) } };
        });
        this.updateState(update(this.mNextState, { rowState }), this.onRowStateChanged);
    }
    visibleAttributes(attributes, attributeStates) {
        const filtered = attributes
            .filter((attribute) => (attribute.condition === undefined || attribute.condition()) &&
            !(this.props.columnBlacklist || []).includes(attribute.id))
            .sort((lhs, rhs) => (lhs.position ?? 100) - (rhs.position ?? 100));
        const enabled = filtered.filter((attribute) => this.getAttributeState(attribute, attributeStates).enabled);
        const attributeSort = (lhs, rhs) => (lhs.position || 100) - (rhs.position || 100);
        return {
            table: enabled
                .filter((attribute) => ["table", "both"].includes(attribute.placement))
                .sort(attributeSort),
            detail: filtered
                .filter((attribute) => ["detail", "both"].includes(attribute.placement))
                .sort(attributeSort),
            inline: enabled
                .filter((attribute) => attribute.placement === "inline")
                .sort(attributeSort),
        };
    }
    getAttributeState(attribute, attributeStatesIn) {
        const attributeStates = attributeStatesIn || this.getAttributeStates(this.props) || {};
        const defaultVisible = attribute.isDefaultVisible !== undefined
            ? attribute.isDefaultVisible
            : true;
        return {
            enabled: defaultVisible,
            sortDirection: "none",
            ...attributeStates[attribute.id],
        };
    }
    setSortDirection = (id, direction) => {
        const { objects, onSetAttributeSort, tableId } = this.props;
        // reset all other columns because we can't really support multisort with this ui
        for (const testId of objects.map((attribute) => attribute.id)) {
            const attrState = this.getAttributeState(objects.find((attribute) => attribute.id === testId));
            if (id !== testId && attrState.sortDirection !== "none") {
                onSetAttributeSort(tableId, testId, "none");
            }
        }
        onSetAttributeSort(tableId, id, direction);
    };
    setGroup = (id) => {
        const { groupBy, onSetGroupingAttribute, tableId } = this.props;
        if (groupBy === id) {
            onSetGroupingAttribute(tableId, undefined);
        }
        else {
            onSetGroupingAttribute(tableId, id);
        }
    };
    setFilter = (attributeId, filter) => {
        const { onSetAttributeFilter, tableId } = this.props;
        onSetAttributeFilter(tableId, attributeId, filter);
        this.deselectAll();
    };
    clearFilters = () => {
        this.setFilter();
    };
    useMultiSelect() {
        // default to true
        return this.props.multiSelect !== false;
    }
    updateState(newState, callback) {
        if (_.isEqual(newState, this.state)) {
            if (callback !== undefined) {
                callback();
            }
            return;
        }
        this.mNextState = newState;
        if (this.mMounted) {
            this.setState(newState, callback);
        }
    }
}
const emptyObj = {};
const emptyList = [];
function mapStateToProps(state, ownProps) {
    let groupBy = state.settings.tables[ownProps.tableId]?.groupBy;
    if ((ownProps.columnBlacklist ?? []).includes(groupBy)) {
        groupBy = undefined;
    }
    return {
        // Defensive check: interface might not be initialized during hydration
        language: state.settings?.interface?.language ?? "en",
        attributeState: getSafe(state, ["settings", "tables", ownProps.tableId, "attributes"], emptyObj),
        filter: state.settings.tables[ownProps.tableId]?.filter,
        groupBy,
        collapsedGroups: getSafe(state, ["settings", "tables", ownProps.tableId, "collapsedGroups"], emptyList),
    };
}
function mapDispatchToProps(dispatch) {
    return {
        onSetAttributeVisible: (tableId, attributeId, visible) => dispatch(setAttributeVisible(tableId, attributeId, visible)),
        onSetAttributeSort: (tableId, attributeId, dir) => dispatch(setAttributeSort(tableId, attributeId, dir)),
        onSetAttributeFilter: (tableId, attributeId, filter) => dispatch(setAttributeFilter(tableId, attributeId, filter)),
        onSetGroupingAttribute: (tableId, attributeId) => dispatch(setGroupingAttribute(tableId, attributeId)),
        onCollapseGroup: (tableId, groupId, collapse) => dispatch(collapseGroup(tableId, groupId, collapse)),
        onSetCollapsedGroups: (tableId, collapsed) => dispatch(setCollapsedGroups(tableId, collapsed)),
    };
}
function registerTableAttribute(instanceGroup, group, attribute) {
    if (instanceGroup === group) {
        return attribute;
    }
    else {
        return undefined;
    }
}
function getTableState(state, tableId) {
    return state.settings.tables[tableId];
}
export function makeGetSelection(tableId) {
    const getTableStateInst = (state) => getTableState(state, tableId);
    return createSelector(getTableStateInst, (tableState) => {
        return Object.keys(tableState.rows).filter((rowId) => tableState.rows[rowId].selected);
    });
}
export default translate(["common"])(extend(registerTableAttribute, "tableId")(connect(mapStateToProps, mapDispatchToProps)(SuperTable)));
